import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Preview, Product } from '@accounts/domain';
import { opening } from './home/invitations';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Waiting } from '@beyond-js/ui/react';
import { Card, Heading, Outage, Problem, Wordmark } from '@accounts/ui';

/**
 * Accepting an invitation.
 *
 * The link is reachable without a session, because the person it was sent to may not have an account
 * yet. What it shows before they sign in is deliberately thin — the organization and the standing —
 * and never whether the address is registered, so the link cannot be used to find out who has an
 * account here.
 *
 * The invitation survives signing in: signing in is asked with this screen as its destination
 * (`/signin?to=/invitations/<secret>`), so a person who arrives, signs in, by any method, lands on this
 * same screen with the invitation still waiting. A screen that cannot show an invitation, or an
 * acceptance refused for the address, always offers a way on.
 */
export function InvitationAcceptance() {
	const { secret = '' } = useParams();
	const { text, standing, identity, accounts, organizations, refresh, settle, language } = usePresence();
	const navigate = useNavigate();

	const [preview, setPreview] = useState<Preview>();
	const [products, setProducts] = useState<Product[]>([]);
	const [reading, setReading] = useState(true);
	// Why the invitation could not be read; a failed acceptance is `failure`
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [busy, setBusy] = useState(false);
	// The acceptance was refused because the person signed in is not the one invited
	const [elsewhere, setElsewhere] = useState(false);
	const here = `/invitations/${encodeURIComponent(secret)}`;

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(async () => {
		setReading(true);
		setUnread(Refusal.kept);
		try {
			setPreview(await organizations.preview(secret));
			setUnread(undefined);
			return true;
		} catch (error) {
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
			setUnread(given);
			return !given.unreachable;
		} finally {
			setReading(false);
		}
	}, [organizations, secret]);

	useEffect(() => {
		void read();
		accounts.providers().then(found => setProducts(found.products), () => undefined);
	}, [read, accounts]);

	async function accept() {
		setBusy(true);
		setFailure(undefined);
		setElsewhere(false);
		try {
			await organizations.accept(secret);
			await refresh();
			// Where they joined, with the product they were invited from (ARR-06)
			setPreview(current => (current ? { ...current, state: 'accepted' } : current));
		} catch (error) {
			// Withheld by the page's session, which answers the person
			if (Refusal.is(error) && error.quiet) return;
			setFailure(error);
			setElsewhere(Refusal.is(error) && error.code === 'ADDRESS_UNVERIFIED');
			// Nothing came back, so the invitation may be accepted: standing and the invitation are read again
			if (Refusal.is(error) && error.unknown) await Promise.all([refresh(), read()]);
		} finally {
			setBusy(false);
		}
	}

	/** Signs out here, keeping this screen, so the invited person can sign in with their own address */
	async function depart() {
		setBusy(true);
		await accounts.leave().catch(() => undefined);
		await settle();
		setBusy(false);
		setElsewhere(false);
		setFailure(undefined);
	}

	// Nothing to accept here: the way on is the overview for a signed-in person, and signing in otherwise
	const onward =
		standing === 'in' ? (
			<Link to="/" className="self-start underline">
				{text('invitation.onward')}
			</Link>
		) : (
			<Link to="/signin" className="self-start underline">
				{text('signin.title')}
			</Link>
		);

	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<Wordmark label="Accounts" />

			{reading && !unread ? <Waiting language={language} kind="lines" rows={3} /> : null}
			{/* When the invitation cannot be read, the H1 names the place (D158) */}
			{unread ? <Heading title={text('place.invitation')} /> : null}
			{/* The invitation is the page's one read: Accounts not answering it is the page's body (D163) */}
			<Outage whole reads={[{ key: 'invitation', title: text('outage.invitation'), unread, run: read }]} />
			{unread && !unread.unreachable && !reading ? (
				<>
					<Callout tone="danger" live title={unread.status === 404 ? text('invitation.unknown') : text(unread.key)} />
					{onward}
				</>
			) : null}
			{failure ? <Problem error={failure} check={read} /> : null}

			{preview?.state === 'accepted' ? <Joined preview={preview} product={products.find(product => product.id === preview.product)} /> : null}

			{preview && preview.state !== 'accepted' ? (
				<>
					<Heading
						title={text('invitation.title', { organization: preview.name })}
						lead={preview.inviter ? text('invitation.by', { inviter: preview.inviter, role: text(`role.${preview.role}`) }) : text('invitation.lead', { role: text(`role.${preview.role}`) })}
					/>
					<Card>
						{preview.product ? <p className="mb-2 text-body text-ink-muted">{text('invitation.from', { product: products.find(product => product.id === preview.product)?.name ?? preview.product })}</p> : null}
						{/* A signed-in person who already holds the address is not told to sign in with it (ACC-24) */}
						{identity?.account.email?.toLowerCase() === preview.email.toLowerCase() ? null : <p className="max-w-reading text-body text-ink-muted">{text('invitation.address', { email: preview.email })}</p>}
						<div className="mt-4">
							{standing === 'in' ? (
								<Button variant="primary" busy={busy} labels={{ busy: text('action.saving') }} onClick={() => void accept()}>
									{text('invitation.accept')}
								</Button>
							) : (
								// The invited address travels in the navigation's state, never in the address, so the
								// sign-in form starts with it (CNT-02)
								<Button variant="primary" onClick={() => navigate(`/signin?to=${encodeURIComponent(here)}`, { state: { email: preview.email } })}>
									{text('invitation.signin')}
								</Button>
							)}
						</div>
						{/* Refused for the address: signing out is a row of its own, and the way on is the card's last
						    line, a paragraph the card ends on at its baseline (D184) */}
						{elsewhere ? (
							<>
								<div className="mt-4">
									<Button variant="secondary" busy={busy} labels={{ busy: text('leave.working') }} onClick={() => void depart()}>
										{text('invitation.signin.other')}
									</Button>
								</div>
								<p className="mt-2 text-body">{onward}</p>
							</>
						) : null}
					</Card>
				</>
			) : null}
		</div>
	);
}

/**
 * Where an accepted invitation led: the organization, and the product it came from in it (ARR-06, ACC-24). The
 * product's button is a row of its own, and the organization's link, with a name of any length, is always the
 * card's last line: a paragraph the card ends on at its baseline (D184)
 */
function Joined({ preview, product }: { preview: Preview; product?: Product }) {
	const { text } = usePresence();
	const open = opening(product, preview.organization);
	return (
		<Card>
			<p className="text-body text-ink">{text('invitation.joined', { organization: preview.name })}</p>
			{open && product ? (
				<div className="mt-4">
					<Button variant="primary" href={open} label={text('received.open', { product: product.name })} />
				</div>
			) : null}
			<p className={`${open && product ? 'mt-3' : 'mt-4'} text-body`}>
				<Link className="underline" to={`/organizations/${encodeURIComponent(preview.organization)}`}>
					{text('received.go', { organization: preview.name })}
				</Link>
			</p>
		</Card>
	);
}
